import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { loadSwapReferenceData } from "./helper/tradeFromScratchHelper";
import tradeFromScratchStore from "@/store/tradeFromScratchStore";
import { sendSwapTicket } from "@/store/tradeFromScratchStoreACTIONS";
import {
  returnHighlightedKeys,
  useTicketShell,
} from "./helper/useTicketShell";
import SwapCoreFields from "./swap/SwapCoreFields";
import SwapDetailFields from "./swap/SwapDetailFields";
import { useSwapTicket } from "./swap/useSwapTicket";
import TicketStatusLine from "./ticketFields/TicketStatusLine";
import TicketHeader from "./ticketFields/TicketHeader";

// LQC-2674 - the Leg Creation swap ticket. A new ticket needs only nominal, direction,
// maturity and the fixed rate; every leg convention is defaulted per currency.

interface ISwapTicketProps {
  ticketId: string;
}

const SwapTicket = ({ ticketId }: ISwapTicketProps) => {
  const { user, getAccessTokenSilently } = useAuth0();
  const shell = useTicketShell(ticketId);
  const { missingEntries, dateIssue } = useSwapTicket(ticketId);
  const ensureTicket = tradeFromScratchStore((state) => state.ensureSwapTicket);
  const resetTicket = tradeFromScratchStore((state) => state.resetSwapTicket);

  useEffect(() => {
    ensureTicket(ticketId);
  }, [ticketId, ensureTicket]);

  useEffect(() => {
    loadSwapReferenceData(getAccessTokenSilently);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canSend =
    missingEntries.length === 0 && !dateIssue && !shell.isSending;
  const highlightedKeys = returnHighlightedKeys(
    shell.isHoveringSend,
    canSend,
    missingEntries,
  );

  const handleSend = async () => {
    const sent = await sendSwapTicket({ ticketId, user, getAccessTokenSilently });
    if (sent) shell.markSent();
  };

  return (
    <div
      data-testid="SwapTicketContainer"
      id="SwapTicketContainer"
      className="flex h-full w-full flex-col gap-2"
    >
      <TicketHeader
        testIdPrefix="SwapTicket"
        instrument={shell.instrument}
        onInstrumentChange={shell.setInstrument}
        canSend={canSend}
        isSending={shell.isSending}
        isConfirming={shell.isConfirmingSend}
        onSend={handleSend}
        onClear={() => resetTicket(ticketId)}
        onSendHoverChange={shell.setIsHoveringSend}
      />
      <TicketStatusLine
        testIdPrefix="SwapTicket"
        lastSent={shell.lastSent}
        missingFields={missingEntries.map((entry) => entry.label)}
        hasDateIssue={!!dateIssue}
        isBlocking={shell.isHoveringSend && !canSend}
      />
      <SwapCoreFields ticketId={ticketId} highlightedKeys={highlightedKeys} />
      <SwapDetailFields ticketId={ticketId} highlightedKeys={highlightedKeys} />
    </div>
  );
};

export default SwapTicket;
